'use client';
import { useEffect } from 'react';

import { useQuery } from '@apollo/experimental-nextjs-app-support/ssr';
import {
  ApolloError,
  QueryResult,
  DocumentNode,
  QueryHookOptions,
  OperationVariables,
  TypedDocumentNode,
} from '@nerdwallet/apollo';
import { useFeatureFlag } from '@nerdwallet/features';

import type { Logger } from '@lib/logging';

const parseApolloError = (error: ApolloError): string => {
  if (!error) {
    return null;
  }
  const errorMessage: string[] = [];
  if (error.message) {
    errorMessage.push(`[Error message]: ${error.message}`);
  }
  if (error.graphQLErrors) {
    const graphQlErrors: string[] = [];
    error.graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      const errorCode = extensions?.code;
      graphQlErrors.push(
        `[GraphQLError]: Error Code: ${errorCode}, Message: ${message}, Location: ${locations}, Path: ${path}`,
      );
    });
    errorMessage.push(...graphQlErrors);
  }
  if (error.networkError) {
    errorMessage.push(
      `[Network ${error.networkError.name}]: Message: ${error.networkError.message}`,
    );
  }
  return errorMessage.join('\n');
};

const parseQuery = (query: DocumentNode): string => {
  const operationDefinitions: string[] = [];
  query.definitions.forEach((node) => {
    if (node.kind === 'OperationDefinition' && node.name?.value) {
      operationDefinitions.push(node.name.value);
    }
  });
  return operationDefinitions.join(', ');
};

/**
 * Wrapper hook for useQuery which logs an error, if an non-authentication error occurs.
 *
 * @param logger - Specific logger instance to log an error with
 * @param query - The GraphQL query
 * @param options - Options to pass in to useQuery
 */
export const useQueryWithLog = <TData = any, TVariables = OperationVariables>(
  logger: Logger,
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<any, any>,
): QueryResult<TData, TVariables> => {
  const results = useQuery(query, options);
  const { error } = results;

  useEffect(() => {
    if (error) {
      const errorMsg = parseApolloError(error);
      const querySummary = parseQuery(query);

      // We only want to log non-authentication errors
      if (
        !(
          errorMsg.includes('AUTHENTICATION_ERROR') &&
          typeof window !== 'undefined'
        )
      ) {
        logger.error(`[useQuery] ${querySummary}\n${errorMsg}`);
      }
    }
  }, [error, logger, query]);

  return results;
};

/**
 * Wrapper hook for useFeatureFlag which logs an error, if an error occurs.
 *
 * @param logger - Specific logger instance to log an error with
 * @param featureFlagName - The feature flag to fetch data for
 */
export const useFeatureFlagWithLog = (
  logger: Logger,
  featureFlagName: string,
): any => {
  const results = useFeatureFlag({ featureFlagName });
  const { error } = results;

  useEffect(() => {
    if (error) {
      logger.error(`[useFeatureFlag] ${featureFlagName}`, error);
    }
  }, [error, logger, featureFlagName]);

  return results;
};
